import React from "react";
import usePageTitle from "../hooks/usePageTitle";

const RefundPolicy = () => {
  usePageTitle("Refund & Return Policy - Agrolinka");

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
        <h1 className="text-3xl font-bold text-agBlack mb-2">
          Refund & Return Policy
        </h1>
        <p className="text-sm text-agLightGray mb-8">
          Last updated: 21/1/2026
        </p>

        <div className="space-y-6 text-gray-700">
          <p>
            At Agrolinka, we want you to be satisfied with your purchase. This
            Refund & Return Policy explains your rights and responsibilities
            when returning products bought from{" "}
            <a
              href="https://agrolinka.com/"
              className="text-agGreen hover:underline"
            >
              https://agrolinka.com/
            </a>{" "}
            ("Website"). By placing an order with us, you agree to the terms
            below.
          </p>

          {/* Section 1 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              1. Product Categories & Eligibility
            </h2>
            <p className="mb-2">We sell two main types of products:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Packaged Dried Food Products</strong> — such as dried
                foods, packaged agricultural goods.
              </li>
              <li>
                <strong>Fresh Farm Produce</strong> — freshly harvested products
                that are perishable.
              </li>
            </ul>
            <p className="mt-3">
              Because of their differing nature, each category has its own
              return and refund rules.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              2. Packaged Dried Food Products (Returns & Refunds)
            </h2>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-agBlack mb-2">
                  Return Window:
                </h3>
                <p>
                  Packaged dried food products may be returned within{" "}
                  <strong>7 days of delivery</strong>, provided they meet the
                  conditions below.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-agBlack mb-2">
                  Conditions for Return:
                </h3>
                <p className="mb-2">
                  To be eligible for a refund or exchange, the item must:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Be unused and in its original packaging.</li>
                  <li>
                    Be returned with all tags, labels, and accessories intact.
                  </li>
                  <li>
                    Be accompanied by your order number and proof of purchase.
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-agBlack mb-2">
                  How to Request a Return:
                </h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    Contact us at{" "}
                    <a
                      href="mailto:support@agrolinka.com"
                      className="text-agGreen hover:underline"
                    >
                      support@agrolinka.com
                    </a>{" "}
                    within 7 days of delivery with your order number and reason
                    for return.
                  </li>
                  <li>
                    We will review your request and provide instructions on how
                    to return the item.
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-agBlack mb-2">
                  Refund Processing:
                </h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    Once we receive and inspect the returned item, we'll notify
                    you about the approval of your refund.
                  </li>
                  <li>
                    If approved, we will process the refund to your original
                    payment method within 5–10 business days after inspection.
                  </li>
                  <li>
                    Shipping charges are non-refundable unless the return was
                    due to our error (e.g., incorrect or defective item).
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              3. Fresh Farm Produce (Final Sale)
            </h2>
            <p className="mb-3">
              Due to the perishable nature of fresh produce and health and
              safety considerations, fresh farm produce is{" "}
              <strong>final sale</strong> once it has been accepted at delivery.
              This means:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
              <li>
                No returns or refunds are provided for fresh farm produce once
                the delivery has been received and accepted by the customer.
              </li>
              <li>
                Please inspect your order upon delivery and inform the delivery
                agent of any obvious issues before accepting the items.
              </li>
            </ul>

            <div className="mt-4">
              <h3 className="font-semibold text-agBlack mb-2">Exceptions:</h3>
              <p className="mb-2">
                If there is a significant quality concern (e.g., the produce is
                spoiled, damaged, or does not match the description), you must:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Contact us within 24 hours of delivery.</li>
                <li>
                  Provide photos and detailed information about the issue.
                </li>
                <li>
                  We will review these cases individually to determine if a
                  partial refund, replacement, or other resolution is
                  appropriate.
                </li>
              </ul>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              4. Damaged or Defective Items
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                If you receive any product (packaged or fresh) that is damaged
                or defective, please contact us within 24 hours of delivery with
                photos of the damage and your order details.
              </li>
              <li>
                We may request additional information to assess the issue and
                determine the appropriate solution.
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              5. Return Shipping Costs
            </h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                Unless the return is due to our error (e.g., we delivered the
                wrong item), return shipping costs are the responsibility of the
                customer.
              </li>
              <li>
                We recommend using a trackable shipping service for returns.
              </li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              6. Late or Missing Refunds
            </h2>
            <p className="mb-2">
              If you haven't received your refund after approval:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                Check your bank or payment account again, as processing times
                may vary.
              </li>
              <li>Contact your payment provider.</li>
              <li>
                If you still haven't received the refund, contact us at{" "}
                <a
                  href="mailto:support@agrolinka.com"
                  className="text-agGreen hover:underline"
                >
                  support@agrolinka.com
                </a>{" "}
                for assistance.
              </li>
            </ul>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-xl font-semibold text-agBlack mb-3">
              7. Contact Information
            </h2>
            <p className="mb-3">
              If you have any questions about this Refund & Return Policy or
              need help with a return or refund, contact us:
            </p>
            <div className="bg-agBg p-4 rounded-md space-y-2">
              <p>
                <strong>📧 Email:</strong>{" "}
                <a
                  href="mailto:info@agrolinka.com"
                  className="text-agGreen hover:underline"
                >
                  info@agrolinka.com
                </a>
              </p>
              <p>
                <strong>📞 Phone:</strong>{" "}
                <a
                  href="tel:+2348065165064"
                  className="text-agGreen hover:underline"
                >
                  +234 806 516 5064
                </a>
              </p>
              <p>
                <strong>🏢 Address:</strong> 13 Akure Street, War College
                Estate, Gwarimpa, Abuja, Nigeria
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
